import classNames from 'classnames';
import { FC, useMemo } from 'react';
import { PromoCodeTimerFeature } from '../PromoCodeTimerFeature/PromoCodeTimerFeature';
import { AppHeader, IconArrowLeft, IconButton } from '@mindly/ui-components';
import { useDispatch, useSelector } from 'react-redux';
import { selectPromoCodeTimerData } from 'src/redux/promoCodes/promoCodes.selectors';
import { safeParse } from 'src/utils';
import { DateTime } from 'luxon';
import { getOnBoardingFlowById } from 'src/api/onBoardingFlow';
import { setTimerData } from 'src/redux/promoCodes/promoCodes.reducer';
import { useAuthFlow } from 'src/shared';
import { setOnBoardingData } from 'src/redux/auth/reducer';
import { onboardingDataSelector } from 'src/redux/auth/selector';
import { useIonViewWillEnter } from '@ionic/react';

type Props = {
  backButtonLink?: string;
  setIsOnboardingDataLoading?: (isLoading: boolean) => void;
};

const AuthPageHeaderFeature: FC<Props> = ({ children, backButtonLink, setIsOnboardingDataLoading }) => {
  const timerData = useSelector(selectPromoCodeTimerData);
  const onboardingData = useSelector(onboardingDataSelector);
  const dispatch = useDispatch();
  const { authUser } = useAuthFlow();
  const newOnBoardingData = sessionStorage.getItem('newOnBoardingDataState');
  const flowId = new URLSearchParams(window.location.search).get('flowId');

  const showPromoCodeTimer = useMemo(() => {
    return !!timerData?.endDate;
  }, [timerData?.endDate]);

  const fetchOnboardingData = async (onboardingId: string) => {
    try {
      setIsOnboardingDataLoading?.(true);
      const response = await getOnBoardingFlowById(onboardingId);
      dispatch(
        setOnBoardingData({
          theme: response.data.theme,
          theme_v2: response.data.theme_v2,
        })
      );

      const duration = response.data.duration_min ?? response.data.promocode_duration_min ?? 0;
      if (duration && response.data.promocode_percent) {
        const newEndDate = DateTime.now().plus({ minutes: duration }).toMillis();
        dispatch(setTimerData({ endDate: newEndDate, percentOff: response.data.promocode_percent }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsOnboardingDataLoading?.(false);
    }
  };

  useIonViewWillEnter(() => {
    const onboardingId = flowId ?? safeParse<{ id: string }>(newOnBoardingData ?? '')?.id ?? '';

    if (authUser && onboardingId && !onboardingData) {
      fetchOnboardingData(onboardingId);
    }
  }, [onboardingData, authUser, newOnBoardingData, flowId]);

  return (
    <AppHeader
      className={classNames({
        'before:content-[""] before:absolute before:bottom-0 before:left-0 before:w-full before:h-[calc(100%-61px)] before:bg-color-background-white !bg-badges-info-bg mb-2':
          showPromoCodeTimer,
      })}
      toolbarClassName={classNames({ '![--background:transparent]': showPromoCodeTimer })}
    >
      {showPromoCodeTimer && (
        <PromoCodeTimerFeature expandWidth endDate={timerData?.endDate} percentOff={timerData?.percentOff} />
      )}
      {children}
      {backButtonLink && <IconButton icon={<IconArrowLeft />} routerLink={backButtonLink} />}
    </AppHeader>
  );
};

export default AuthPageHeaderFeature;
